import React, { useState, useEffect, useCallback } from 'react';
import { isDemoMode } from '../utils/demoMode';
// Import searchFranchisees service
import { listFranchisees, deleteFranchisee, searchFranchisees, resetFranchiseePassword, updateFranchiseeStatus } from '../services/franchiseeService';
// Added FaSearch and FaTimes icons
import { FaPlus, FaEdit, FaTrashAlt, FaEnvelopeOpenText, FaSearch, FaTimes, FaKey, FaToggleOn, FaToggleOff, FaCopy, FaSpinner } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import ConfirmationModal from '../components/ConfirmationModal';
import StatusChangeModal from '../components/StatusChangeModal';

const PAGE_SIZE = 20;

const FranchiseesPage = () => {
  const [franchisees, setFranchisees] = useState([]);
  const [loading, setLoading] = useState(true); // General loading for fetch operations
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);
  const [apiError, setApiError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [franchiseeToDelete, setFranchiseeToDelete] = useState(null);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [franchiseeToResetPassword, setFranchiseeToResetPassword] = useState(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false); // For "Searching..." text, tied to search term length
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [franchiseeToUpdateStatus, setFranchiseeToUpdateStatus] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // Track which franchisee is being updated

  // --- Filter and Copy Emails State ---
  const [filterType, setFilterType] = useState('all'); // 'all' | 'regional' | 'external'
  const [copyingEmails, setCopyingEmails] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageDocId, setNextPageDocId] = useState(null);
  const [pageStartDocIdsHistory, setPageStartDocIdsHistory] = useState([null]);
  const [isPaginatingAction, setIsPaginatingAction] = useState(false); // True during pagination button click action

  // Unified data fetching function
  const fetchFranchiseesData = useCallback(async (term, pageDocIdForFetch) => {
    setLoading(true);
    setError(null);
    // setIsSearching is primarily for the text message, driven by term length
    setIsSearching(term.length >= 3);

    try {
      let apiData;
      if (term.length >= 3) {
        apiData = await searchFranchisees({
          searchString: term,
          pageSize: PAGE_SIZE,
          startAfterDocId: pageDocIdForFetch,
        });
      } else if (term.length === 0) {
        apiData = await listFranchisees(PAGE_SIZE, pageDocIdForFetch);
      } else {
        setLoading(false); // Set loading false before returning for short terms
        setIsSearching(false); // Explicitly set searching false
        return { newFranchisees: [], newNextPageDocId: null };
      }
      return {
        newFranchisees: apiData.franchisees || [],
        newNextPageDocId: apiData.nextPageDocId || null,
      };
    } catch (err) {
      setError(err.message || 'Failed to load franchisees.');
      return { newFranchisees: [], newNextPageDocId: null }; // Return empty on error
    } finally {
      setLoading(false); // Ensure loading is always reset
    }
  }, [listFranchisees, searchFranchisees]);

  // Debounced search handler
  const debouncedSearchHandler = useCallback(
    debounce(async (term) => {
      setCurrentPage(1);
      setPageStartDocIdsHistory([null]);
      // fetchFranchiseesData is async and returns data
      const result = await fetchFranchiseesData(term, null);
      setFranchisees(result.newFranchisees);
      setNextPageDocId(result.newNextPageDocId);
    }, 500),
    [fetchFranchiseesData, setCurrentPage, setPageStartDocIdsHistory, setFranchisees, setNextPageDocId] // Added state setters to dependencies
  );

  useEffect(() => {
    // Initial fetch or fetch when searchTerm changes via debounce
    debouncedSearchHandler(searchTerm);
    return () => {
      debouncedSearchHandler.cancel();
    };
  }, [searchTerm, debouncedSearchHandler]);

  useEffect(() => {
    let timer;
    if (successMessage || apiError) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setApiError(null);
        if (successMessage && location.state?.message) {
            navigate(location.pathname, { replace: true, state: {} });
        }
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage, apiError, navigate, location.pathname, location.state?.message]);

  const handleAddFranchisee = () => navigate('/franchisees/add');
  const handleEdit = (id) => navigate(`/franchisees/edit/${id}`);

  const handleDelete = (franchisee) => {
    setFranchiseeToDelete(franchisee);
    setIsModalOpen(true);
    setApiError(null);
  };

  const handleResetPassword = (franchisee) => {
    setFranchiseeToResetPassword(franchisee);
    setIsResetPasswordModalOpen(true);
    setApiError(null);
  };

  const handleStatusToggle = (franchisee) => {
    // Normalize isActive: treat undefined or true as active (true), only false is inactive
    const currentStatus = franchisee.isActive !== false;
    const newStatus = !currentStatus;
    setFranchiseeToUpdateStatus(franchisee);
    setPendingStatusChange(newStatus);
    setIsStatusModalOpen(true);
    setApiError(null);
  };

  const confirmStatusChange = async () => {
    if (!franchiseeToUpdateStatus || pendingStatusChange === null) return;

    const newStatus = pendingStatusChange;
    setStatusUpdateLoading(franchiseeToUpdateStatus.id);

    try {
      await updateFranchiseeStatus(franchiseeToUpdateStatus.id, newStatus);

      // Update the franchisee in the local state
      setFranchisees(prevFranchisees =>
        prevFranchisees.map(fse =>
          fse.id === franchiseeToUpdateStatus.id
            ? { ...fse, isActive: newStatus }
            : fse
        )
      );

      setSuccessMessage(`Franchisee ${newStatus ? 'activated' : 'suspended'} successfully.`);

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);

      // Close modal
      setIsStatusModalOpen(false);
      setFranchiseeToUpdateStatus(null);
      setPendingStatusChange(null);

    } catch (error) {
      console.error('Error updating franchisee status:', error);
      setApiError(error.message || 'Failed to update franchisee status.');

      // Clear error message after 5 seconds
      setTimeout(() => setApiError(null), 5000);
    } finally {
      setStatusUpdateLoading(null);
    }
  };

  const cancelStatusChange = () => {
    setIsStatusModalOpen(false);
    setFranchiseeToUpdateStatus(null);
    setPendingStatusChange(null);
    setApiError(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFranchiseeToDelete(null);
  };

  const closeResetPasswordModal = () => {
    setIsResetPasswordModalOpen(false);
    setFranchiseeToResetPassword(null);
  };

  // confirmDelete - MODIFIED
  const confirmDelete = async () => {
    if (!franchiseeToDelete) return;
    try {
      await deleteFranchisee(franchiseeToDelete.id);
      closeModal();
      setSuccessMessage(`Franchisee '${franchiseeToDelete.franchiseName || franchiseeToDelete.fseCode}' deleted successfully.`);
      const docIdForCurrentPageRefresh = pageStartDocIdsHistory[currentPage - 1];
      
      const result = await fetchFranchiseesData(searchTerm, docIdForCurrentPageRefresh);
      setFranchisees(result.newFranchisees);
      setNextPageDocId(result.newNextPageDocId);
      // If the current page becomes empty and it's not the first page, user can navigate back.
      // Or, implement auto-navigate to previous page if desired (more complex UX).
    } catch (err) {
      setApiError(err.message || 'Failed to delete franchisee.');
      closeModal();
    }
  };

  // confirmResetPassword - UPDATED
  const confirmResetPassword = async () => {
    if (!franchiseeToResetPassword) return;
    setIsResettingPassword(true);
    try {
      await resetFranchiseePassword(franchiseeToResetPassword.email);
      setSuccessMessage(`Password reset successfully for '${franchiseeToResetPassword.franchiseName || franchiseeToResetPassword.fseCode}'. The franchisee will receive an email with their temporary password.`);
    } catch (err) {
      setApiError(err.message || 'Failed to reset password.');
    } finally {
      setIsResettingPassword(false);
      closeResetPasswordModal();
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // --- Pagination Handlers --- MODIFIED
  const handleNextPage = useCallback(async () => {
    if (!nextPageDocId || loading) {
      return;
    }
    setIsPaginatingAction(true);
    const docIdForNextFetch = nextPageDocId;

    try {
      const result = await fetchFranchiseesData(searchTerm, docIdForNextFetch);

      // Ensure result and newFranchisees are valid before proceeding
      if (result && result.newFranchisees && result.newFranchisees.length > 0) {
        setFranchisees(result.newFranchisees);
        setNextPageDocId(result.newNextPageDocId); // This could be null if the new page is the last one
        setCurrentPage(prevPage => prevPage + 1);
        setPageStartDocIdsHistory(prevHistory => [...prevHistory, docIdForNextFetch]);
      } else {
        // Next page was empty or result was invalid. Do not change franchisees list.
        // Mark that there's no further next page from this point.
        setNextPageDocId(null);
        // Current page, franchisees list, and history remain unchanged.
      }
    } catch (error) {
      // Error state is set by fetchFranchiseesData. Log if needed or handle specific UI.
      console.error("Error during pagination fetch:", error);
      setNextPageDocId(null); // Ensure next is disabled on error too
    } finally {
      setIsPaginatingAction(false);
    }
  }, [nextPageDocId, loading, searchTerm, fetchFranchiseesData, setCurrentPage, setPageStartDocIdsHistory, setFranchisees, setNextPageDocId]);

  const handlePrevPage = useCallback(async () => {
    if (currentPage <= 1 || loading) {
      return;
    }
    setIsPaginatingAction(true);
    const targetPage = currentPage - 1;
    const docIdForPrevFetch = pageStartDocIdsHistory[targetPage - 1];

    try {
      const result = await fetchFranchiseesData(searchTerm, docIdForPrevFetch);
      // Even if result.newFranchisees is empty (shouldn't happen for a valid prev page),
      // we update, as the user explicitly navigated back.
      setFranchisees(result.newFranchisees || []); 
      setNextPageDocId(result.newNextPageDocId); 
      setCurrentPage(targetPage);
      setPageStartDocIdsHistory(prevHistory => prevHistory.slice(0, targetPage));
    } catch (error) {
      console.error("Error during previous page fetch:", error);
      // Potentially revert optimistic state updates or show error to user
    } finally {
      setIsPaginatingAction(false);
    }
  }, [currentPage, loading, pageStartDocIdsHistory, searchTerm, fetchFranchiseesData, setCurrentPage, setPageStartDocIdsHistory, setFranchisees, setNextPageDocId]);

  // --- Filtering Helper ---
  const getFilteredFranchisees = useCallback((franchiseesList) => {
    if (filterType === 'all') return franchiseesList;
    if (filterType === 'external') {
      return franchiseesList.filter(fse => fse.isExternal === true);
    }
    if (filterType === 'regional') {
      return franchiseesList.filter(fse => fse.isExternal !== true); // Includes undefined and false
    }
    return franchiseesList;
  }, [filterType]);

  // --- Copy Emails Functionality ---
  const fetchAllFranchisees = async (searchTerm) => {
    const allResults = [];
    let nextPageDocId = null;

    do {
      const result = searchTerm.length >= 3
        ? await searchFranchisees({
            searchString: searchTerm,
            pageSize: 100,
            startAfterDocId: nextPageDocId
          })
        : await listFranchisees(100, nextPageDocId);

      allResults.push(...result.franchisees);
      nextPageDocId = result.nextPageDocId;
    } while (nextPageDocId);

    return allResults;
  };

  const handleCopyEmails = async () => {
    setCopyingEmails(true);
    setApiError(null);

    try {
      // Fetch ALL franchisees (not just current page)
      const allFranchisees = await fetchAllFranchisees(searchTerm);

      // Apply filter
      const filteredFranchisees = getFilteredFranchisees(allFranchisees);

      // Extract emails, excluding inactive franchisees
      const activeEmails = filteredFranchisees
        .filter(fse => fse.email && fse.isActive !== false)
        .map(fse => fse.email);

      const emailList = activeEmails.join(', ');

      // Copy to clipboard
      await navigator.clipboard.writeText(emailList);

      // Show success feedback
      setCopySuccess(true);
      setSuccessMessage(`Copied ${activeEmails.length} email addresses to clipboard (excluded inactive)`);
      setTimeout(() => {
        setCopySuccess(false);
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to copy emails:', err);
      setApiError('Failed to copy emails to clipboard');
    } finally {
      setCopyingEmails(false);
    }
  };

  // Apply filter to displayed franchisees
  const displayedFranchisees = getFilteredFranchisees(franchisees);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans">
      {/* Title Row */}
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">Franchisees</h1>
        <Link to="/" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
            Back to Dashboard
        </Link>
      </div>

      {/* Buttons and Search Row */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            {/* Action Buttons */}
            <div className="flex items-center space-x-4">
                {!isDemoMode() && (
                <button
                    onClick={handleAddFranchisee}
                    className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm inline-flex items-center transition duration-300"
                >
                    <FaPlus className="mr-2" />
                    Add Franchisee
                </button>
                )}
                <Link
                    to="/franchisees/allowed-emails" // Link to the new page
                    className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 hover:text-gray-900 inline-flex items-center transition duration-300"
                >
                    <FaEnvelopeOpenText className="mr-2" />
                    Manage Franchisee Emails
                </Link>

                {/* Copy Emails Button */}
                <button
                    onClick={handleCopyEmails}
                    disabled={copyingEmails}
                    className="bg-white border border-gray-300 text-gray-700 font-semibold py-2 px-5 rounded-full shadow-sm hover:bg-gray-50 hover:text-gray-900 inline-flex items-center transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Copy all filtered emails to clipboard"
                >
                    {copyingEmails ? (
                        <>
                            <FaSpinner className="mr-2 animate-spin" />
                            Copying...
                        </>
                    ) : copySuccess ? (
                        <>
                            <FaCopy className="mr-2" />
                            Copied!
                        </>
                    ) : (
                        <>
                            <FaCopy className="mr-2" />
                            Copy Emails
                        </>
                    )}
                </button>

                {/* Filter Dropdown */}
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="block rounded-full border-0 py-2.5 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 text-sm focus:outline-none focus:ring-2 focus:ring-green-600"
                >
                    <option value="all">All Franchisees</option>
                    <option value="regional">Regional</option>
                    <option value="external">External</option>
                </select>

                
            </div>

            {/* Search Input */}
            <div className="relative flex-grow sm:flex-grow-0 sm:w-64 md:w-72">
                <input
                    type="text"
                    placeholder="Search by franchise name, first/last name or code..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="block rounded-full border-0 py-2.5 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 pl-10 w-full focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                {searchTerm && (
                    <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        aria-label="Clear search"
                    >
                        <FaTimes />
                    </button>
                )}
            </div>
       </div>

      {/* Display Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage}
        </div>
      )}
      {/* Display API Error Message */}
      {apiError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {apiError}
        </div>
      )}

      {/* Loading and Error States */}
      {(loading || isSearching) && !isPaginatingAction && franchisees.length === 0 && (
        <p className="text-center text-gray-500 py-4">
          {isSearching ? 'Searching...' : 'Loading franchisees...'}
        </p>
      )}
      {error && !apiError && (
        <p className="text-center text-red-500 bg-red-100 p-3 rounded mb-4">Error: {error}</p>
      )}
      {!loading && !isSearching && !isPaginatingAction && searchTerm.length > 0 && searchTerm.length < 3 && (
          <p className="text-center text-gray-500 p-6">Please enter at least 3 characters to search.</p>
      )}

      {/* Franchisees Table and Pagination Container */}
      {/* Show if not error and search term is valid (or no search term), and either not loading OR paginating */}
      {((!loading || isPaginatingAction)) && !error && !(searchTerm.length > 0 && searchTerm.length < 3) && (
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl overflow-hidden">
          {franchisees.length === 0 ? (
            // Only show "No franchisees found" if not loading (or if paginating and result is empty)
            // If loading && !isPaginatingAction && franchisees.length === 0, the global loader above handles it.
            (!loading && !isPaginatingAction) && (
                <p className="text-center text-gray-500 p-6">
                    {searchTerm && !(searchTerm.length > 0 && searchTerm.length < 3) ? 'No franchisees found matching your search.' :
                    (searchTerm.length === 0 ? 'No franchisees found.' : 'Please enter at least 3 characters to search.')}
                </p>
            )
          ) : (
            <table className="min-w-full leading-normal">
              <thead>
                <tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm">
                  {/* Added Code Column */}
                  <th className="px-5 py-3 border-b-2 border-gray-200">Code</th>
                  {/* Added Franchise Column */}
                  <th className="px-5 py-3 border-b-2 border-gray-200">Franchise</th>
                  {/* Name column now only for contact person */}
                  <th className="px-5 py-3 border-b-2 border-gray-200">Name</th>
                  {/* Removed Territory Column */}
                  <th className="px-5 py-3 border-b-2 border-gray-200">Email</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200">Phone</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200 text-center">Status</th>
                  <th className="px-5 py-3 border-b-2 border-gray-200 text-center whitespace-nowrap min-w-[120px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedFranchisees.map((fse) => (
                  <tr key={fse.id} className="hover:bg-gray-50">
                    {/* Added Code Data Cell */}
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{fse.fseCode || 'N/A'}</p>
                    </td>
                    {/* Added Franchise Data Cell */}
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{fse.franchiseName || 'N/A'}</p>
                    </td>
                    {/* Modified Name Data Cell */}
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{`${fse.firstName || ''} ${fse.lastName || ''}`.trim() || 'N/A'}</p>
                    </td>
                    {/* Removed Territory Data Cell */}
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{fse.email || 'N/A'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{fse.phoneNumber || 'N/A'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        {fse.isActive !== false ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Suspended
                          </span>
                        )}
                        <button
                          onClick={() => handleStatusToggle(fse)}
                          disabled={statusUpdateLoading === fse.id}
                          className={`ml-2 transition duration-300 ${
                            statusUpdateLoading === fse.id
                              ? 'text-gray-500 cursor-not-allowed'
                              : fse.isActive !== false
                                ? 'text-green-500 hover:text-green-700'
                                : 'text-red-500 hover:text-red-700 transition-colors'
                          }`}
                          title={fse.isActive !== false ? 'Suspend Franchisee' : 'Activate Franchisee'}
                        >
                          {statusUpdateLoading === fse.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                          ) : fse.isActive !== false ? (
                            <FaToggleOn />
                          ) : (
                            <FaToggleOff />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center whitespace-nowrap">
                      {!isDemoMode() && (
                      <button onClick={() => handleEdit(fse.id)} className="text-green-600 hover:text-green-700 transition-colors mr-3 transition duration-300" title="Edit Franchisee">
                        <FaEdit />
                      </button>
                      )}
                      <button onClick={() => handleResetPassword(fse)} className="text-amber-500 hover:text-amber-700 transition-colors mr-3 transition duration-300" title="Reset Password">
                        <FaKey />
                      </button>
                      {/* Pass the whole franchisee object to handleDelete */}
                      {!isDemoMode() && (
                      <button onClick={() => handleDelete(fse)} className="text-red-500 hover:text-red-700 transition-colors transition duration-300" title="Delete Franchisee">
                        <FaTrashAlt />
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Pagination Controls - Show if there are franchisees or a next page is available */}
          {(franchisees.length > 0 || nextPageDocId) && (
            <div className="mt-6 flex justify-center items-center space-x-2 py-4">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1 || loading || isSearching} // `loading` will disable during fetch
                className="px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
              >
                {isPaginatingAction && loading ? 'Loading...' : 'Previous'}
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage}
              </span>
              <button
                onClick={handleNextPage}
                disabled={!nextPageDocId || loading || isSearching} // `loading` will disable during fetch
                className="px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition duration-150"
              >
                {isPaginatingAction && loading ? 'Loading...' : 'Next'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the franchisee \"${franchiseeToDelete?.franchiseName || franchiseeToDelete?.fseCode || ''}\"? This action cannot be undone.`}
      />

      {/* Reset Password Confirmation Modal */}
      <ConfirmationModal
        isOpen={isResetPasswordModalOpen}
        onClose={closeResetPasswordModal}
        onConfirm={confirmResetPassword}
        title="Confirm Password Reset"
        message={`Are you sure you want to reset the password for \"${franchiseeToResetPassword?.franchiseName || franchiseeToResetPassword?.fseCode || ''}\"? A temporary password will be sent to their email address (${franchiseeToResetPassword?.email || ''}) and they will be required to change it on their next login.`}
        isLoading={isResettingPassword}
        loadingText="Resetting password..."
      />

      {/* Status Change Confirmation Modal */}
      <StatusChangeModal
        isOpen={isStatusModalOpen}
        onClose={cancelStatusChange}
        onConfirm={confirmStatusChange}
        franchisee={franchiseeToUpdateStatus}
        newStatus={pendingStatusChange}
        isLoading={statusUpdateLoading === franchiseeToUpdateStatus?.id}
      />
    </div>
  );
};

export default FranchiseesPage;
